import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useBranding } from "../contexts/BrandingContext";
import {
  LayoutDashboard, Users, Settings, LogOut, Moon, Sun, Menu, X,
  Shield, ChevronRight, Ticket, Activity, UserCircle, Search, SlidersHorizontal,
  Calendar, FileText, AlertTriangle, KeyRound, Package
} from "lucide-react";
import api from "../lib/api";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/customers", icon: Users, label: "Kunden" },
  { to: "/tickets", icon: Ticket, label: "Alle Tickets" },
  { to: "/calendar", icon: Calendar, label: "Kalender" },
  { to: "/inventory", icon: Package, label: "Lager" },
];

const ADMIN_NAV = [
  { to: "/users", icon: SlidersHorizontal, label: "Benutzer" },
  { to: "/settings", icon: Settings, label: "Einstellungen" },
];

const SECURITY_NAV = { to: "/security", icon: KeyRound, label: "Sicherheit" };

// ── Global Search ─────────────────────────────────────────────────────
function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef();
  const navigate = useNavigate();
  const timerRef = useRef();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(""); setResults(null); }
  }, [open]);

  const search = useCallback(async (query) => {
    if (query.length < 2) { setResults(null); return; }
    setLoading(true);
    try {
      const r = await api.get("/search", { params: { q: query } });
      setResults(r.data);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQ(val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(val), 300);
  };

  const go = (path) => { navigate(path); setOpen(false); };

  const hasResults = results && (
    results.customers?.length > 0 ||
    results.tickets?.length > 0 ||
    results.docs?.length > 0 ||
    results.events?.length > 0
  );

  const highlight = (text) => {
    if (!text || !q) return text;
    const safe = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${safe})`, "ig"));
    return parts.map((p, i) =>
      p.toLowerCase() === q.toLowerCase()
        ? <mark key={i} className="bg-primary/20 text-foreground font-semibold">{p}</mark>
        : <React.Fragment key={i}>{p}</React.Fragment>
    );
  };

  return (
    <div className="relative">
      <button
        data-testid="global-search-button"
        onClick={() => setOpen(!open)}
        className="p-2 hover:bg-secondary rounded-sm transition-colors"
        title="Suche (Kunden, Tickets, Dokumente, Termine)"
      >
        <Search size={16} />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          {/* Search panel */}
          <div className="absolute right-0 top-10 z-50 w-[28rem] bg-card border border-border shadow-xl">
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border">
              <Search size={14} className="text-muted-foreground flex-shrink-0" />
              <input
                ref={inputRef}
                data-testid="global-search-input"
                type="text"
                value={q}
                onChange={handleChange}
                placeholder="Kunden, Tickets, Dokumente, Termine..."
                className="flex-1 text-sm bg-transparent focus:outline-none"
              />
              {loading && <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />}
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground"><X size={14} /></button>
            </div>

            {/* Results */}
            {q.length >= 2 && (
              <div className="max-h-[28rem] overflow-y-auto">
                {!hasResults && !loading && (
                  <div className="p-4 text-sm text-muted-foreground text-center">Keine Treffer für „{q}"</div>
                )}

                {results?.customers?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground px-3 py-2 bg-muted/30">Kunden</p>
                    {results.customers.map(c => (
                      <button key={c._id} onClick={() => go(`/customers/${c._id}`)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-secondary/60 transition-colors text-left"
                        data-testid={`search-customer-${c._id}`}>
                        <div className="initials-avatar w-7 h-7 text-xs flex-shrink-0">
                          {(c.first_name?.[0] || "") + (c.last_name?.[0] || "")}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{c.first_name} {c.last_name}</p>
                          <p className="text-xs text-muted-foreground">{c.customer_number}{c.company ? ` · ${c.company}` : ""}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {results?.tickets?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground px-3 py-2 bg-muted/30 border-t border-border">Tickets</p>
                    {results.tickets.map(t => (
                      <button key={t._id} onClick={() => go(`/customers/${t.customer_id}`)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-secondary/60 transition-colors text-left"
                        data-testid={`search-ticket-${t._id}`}>
                        <Ticket size={14} className="text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{t.title}</p>
                          <p className="text-xs text-muted-foreground">{t.customer_name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {results?.docs?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground px-3 py-2 bg-muted/30 border-t border-border">Dokumente</p>
                    {results.docs.map(d => (
                      <button key={d._id} onClick={() => go(`/customers/${d.customer_id}?tab=docs`)}
                        className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-secondary/60 transition-colors text-left"
                        data-testid={`search-doc-${d._id}`}>
                        <FileText size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{highlight(d.title)}</p>
                          {d.snippet && (
                            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{highlight(d.snippet)}</p>
                          )}
                          {d.customer_name && (
                            <p className="text-xs text-primary mt-0.5">{d.customer_name}</p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {results?.events?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground px-3 py-2 bg-muted/30 border-t border-border">Termine</p>
                    {results.events.map(e => (
                      <button key={e._id} onClick={() => go("/calendar")}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-secondary/60 transition-colors text-left"
                        data-testid={`search-event-${e._id}`}>
                        <Calendar size={14} className="text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{highlight(e.title)}</p>
                          <p className="text-xs text-muted-foreground">
                            {e.start ? new Date(e.start).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" }) : ""}
                            {e.location ? ` · ${e.location}` : ""}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {q.length === 0 && (
              <div className="p-3 text-xs text-muted-foreground">Mindestens 2 Zeichen eingeben...</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Layout ────────────────────────────────────────────────────────────
export default function Layout({ children, darkMode, setDarkMode }) {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Auto-logout after 30 min of inactivity
  useEffect(() => {
    if (!user) return;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        try { await logout(); } finally { navigate("/login", { replace: true }); }
      }, 30 * 60 * 1000);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [user, logout, navigate]);

  const handleLogout = async () => { await logout(); navigate("/"); };
  const isActive = (to) => location.pathname.startsWith(to);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {sidebarOpen && (
        <div className="fixed inset-0 z-20 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-30 w-56 flex-shrink-0 bg-card border-r border-border flex flex-col transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        {/* Logo */}
        <div className="flex items-center gap-2.5 px-4 h-14 border-b border-border flex-shrink-0">
          {branding?.logo_data_uri ? (
            <img src={branding.logo_data_uri} alt="Logo" className="w-7 h-7 object-contain flex-shrink-0" />
          ) : (
            <div className="w-7 h-7 bg-primary flex items-center justify-center flex-shrink-0">
              <Shield size={14} className="text-primary-foreground" />
            </div>
          )}
          <span className="text-lg font-bold tracking-tight truncate" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} title={branding?.display_name}>
            {branding?.display_name || "DATON"}
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground px-2 py-1.5 mt-1">Navigation</p>
          {NAV.map(({ to, icon: Icon, label }) => (
            <Link key={to} to={to} onClick={() => setSidebarOpen(false)}
              className={`sidebar-link ${isActive(to) ? "active" : ""}`} data-testid={`nav-${label.toLowerCase()}`}>
              <Icon size={15} /><span>{label}</span>
            </Link>
          ))}
          {user?.role === "admin" && (
            <>
              <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground px-2 py-1.5 mt-3">Administration</p>
              {ADMIN_NAV.map(({ to, icon: Icon, label }) => (
                <Link key={to} to={to} onClick={() => setSidebarOpen(false)}
                  className={`sidebar-link ${isActive(to) ? "active" : ""}`}>
                  <Icon size={15} /><span>{label}</span>
                </Link>
              ))}
            </>
          )}
        </nav>

        {/* User footer */}
        <div className="border-t border-border p-3 flex-shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <div className="initials-avatar text-xs">{user?.initials || "??"}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
          <Link to="/profile" onClick={() => setSidebarOpen(false)} className="sidebar-link mb-0.5" data-testid="nav-profile">
            <UserCircle size={14} /><span>Mein Profil</span>
          </Link>
          <Link to="/security" onClick={() => setSidebarOpen(false)} className="sidebar-link mb-0.5" data-testid="nav-security">
            <KeyRound size={14} /><span>Sicherheit</span>
          </Link>
          <button data-testid="logout-button" onClick={handleLogout}
            className="sidebar-link w-full text-destructive hover:text-destructive hover:bg-destructive/10">
            <LogOut size={14} /><span>Abmelden</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="h-14 border-b border-border bg-card flex items-center justify-between px-4 flex-shrink-0">
          <button className="lg:hidden p-1.5 hover:bg-secondary rounded-sm" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <div className="hidden lg:flex items-center gap-1 text-sm text-muted-foreground">
            <span>{branding?.display_name || "DATON"}</span><ChevronRight size={14} />
            <span className="text-foreground font-medium capitalize">{location.pathname.split("/")[1] || "Dashboard"}</span>
          </div>
          <div className="flex items-center gap-1 ml-auto">
            <GlobalSearch />
            <button data-testid="theme-toggle" onClick={() => setDarkMode(!darkMode)}
              className="p-2 hover:bg-secondary rounded-sm transition-colors" title={darkMode ? "Helles Design" : "Dunkles Design"}>
              {darkMode ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </header>
        {user?.must_change_password && (
          <div className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-2.5 flex items-center gap-2 text-sm">
            <AlertTriangle size={14} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <span className="flex-1">
              <strong>Standard-Passwort erkannt.</strong> Bitte ändere dein Passwort, um dein Konto zu schützen.
            </span>
            <Link to="/profile" className="text-xs font-bold uppercase tracking-widest underline">
              Jetzt ändern
            </Link>
          </div>
        )}
        {user?.workspace_require_2fa && !user?.totp_enabled && (
          <div className="bg-primary/10 border-b border-primary/30 px-4 py-2.5 flex items-center gap-2 text-sm" data-testid="enforce-2fa-banner">
            <AlertTriangle size={14} className="text-primary flex-shrink-0" />
            <span className="flex-1">
              <strong>2FA ist in diesem Workspace Pflicht.</strong> Bitte richte jetzt die Zwei-Faktor-Authentifizierung ein.
            </span>
            <Link to="/security" className="text-xs font-bold uppercase tracking-widest underline">
              Jetzt einrichten
            </Link>
          </div>
        )}
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
